import { API_CONFIG } from '../config/api';
import { getStoredUTMs } from '../utils/utm';
import { buildStripeCheckoutMetadata } from '../utils/stripeCheckoutMetadata';
import { buildAuthHeaders, ensureFunnelAccessToken, fetchWithAuthRetry } from '../utils/funnelAuth';

/**
 * Stripe service stub.
 * Provides functions for plan retrieval, subscription creation, payment management,
 * and subscription lifecycle operations.
 */

/** @deprecated Use buildAuthHeaders from funnelAuth — kept as alias for existing call sites. */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
    return buildAuthHeaders(extra);
}

/** Parse backend error payloads — API returns `{ error: { message, code } }`. */
function getApiErrorMessage(errorData: Record<string, unknown>, fallback: string): string {
    if (typeof errorData.message === 'string' && errorData.message) {
        return errorData.message;
    }
    const err = errorData.error;
    if (typeof err === 'string' && err) {
        return err;
    }
    if (err && typeof err === 'object' && typeof (err as { message?: string }).message === 'string') {
        return (err as { message: string }).message;
    }
    return fallback;
}

// ─── Generic API response type ───────────────────────────────────────────────

interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
}

// ─── Plan retrieval ──────────────────────────────────────────────────────────

/**
 * Fetch available plans filtered by type and category.
 *
 * @param type - Plan type filter (e.g. 'monthly', 'one_time')
 * @param category - Plan category filter (e.g. 'primary', 'special', 'feature_unlock', 'walkmore', 'join')
 * @param pricingTier - Walkmore only: L | M | H (ignored for join category on backend)
 */
export async function getPlans(
    type: string,
    category: string,
    pricingTier?: string,
): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams({ type, category });
    if (pricingTier) {
        queryParams.set('pricing_tier', pricingTier);
    }
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_PLANS}?${queryParams.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch plans with status ${response.status}`);
    }

    return response.json();
}

// ─── Stripe checkout metadata (minimal contract) ─────────────────────────────

export interface SubscriptionMetadata {
    /** Canonical acquisition funnel for Stripe analytics: join | walkmore | magic | joinv4 */
    funnel_source?: string;
    ab_variant?: string;
    pricing_mode?: string;
    pricing_tier?: string;
    pricing_band?: string;
    cp_score?: string;
    vp_score?: string;
    /** Dollar pLTV sent to Meta on initial Purchase — persisted for server-side LTV revisions. */
    predicted_ltv?: string;
    card_funding?: string;
    ltv_version?: string;
    tiktok_event_id?: string;
    ttclid?: string;
    fbp?: string;
    fbc?: string;
    pinterest_event_id?: string;
}

function buildMetadataWithUTMs(metadata?: SubscriptionMetadata): Record<string, string> {
    return buildStripeCheckoutMetadata(metadata || {});
}

// ─── Subscription creation ───────────────────────────────────────────────────

/**
 * Create a 1-week trial subscription that transitions to a follow-up plan.
 *
 * @param planId - The trial plan ID
 * @param paymentMethodId - Stripe payment method ID
 * @param transitionPlanId - The plan ID to transition to after the trial
 * @param couponId - Optional coupon/discount ID
 * @param metadata - Optional quiz metadata to attach to the Stripe subscription
 * @returns ApiResponse with subscription data and optional clientSecret for 3DS
 */
export async function create1WeekTrialSubscription(
    planId: string,
    paymentMethodId: string | undefined,
    fourWeekPlanId: string,
    couponId?: string,
    paymentType?: 'card' | 'paypal',
    metadata?: SubscriptionMetadata,
    idempotencyKey?: string
): Promise<ApiResponse<{ clientSecret?: string; subscriptionId?: string }>> {
    try {
        await ensureFunnelAccessToken();

        const utms = getStoredUTMs();
        const body: any = {
            planId,
            fourWeekPlanId,
            ...(utms.utm_source && { utm_source: utms.utm_source }),
            ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
            ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
            ...(utms.utm_content && { utm_content: utms.utm_content }),
            ...(utms.utm_term && { utm_term: utms.utm_term }),
        };
        if (paymentMethodId) body.paymentMethodId = paymentMethodId;
        if (couponId) body.couponId = couponId;
        if (paymentType) body.paymentType = paymentType;
        body.metadata = buildMetadataWithUTMs(metadata);
        if (metadata?.ab_variant) body.ab_variant = metadata.ab_variant;

        const headers: Record<string, string> = {};
        if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

        const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/subscription/1-week-trial`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(getApiErrorMessage(data, 'Failed to create 1-week trial subscription'));
        }

        return data;
    } catch (error: any) {
        console.error('Create 1-week trial subscription error:', error);
        throw error;
    }
}

/**
 * Create a 4-week subscription.
 *
 * @param planId - The plan ID
 * @param paymentMethodId - Stripe payment method ID
 * @param couponId - Optional coupon/discount ID
 * @returns ApiResponse with subscription data and optional clientSecret for 3DS
 */
export async function create4WeekSubscription(
    planId: string,
    paymentMethodId: string | undefined,
    couponId?: string,
    paymentType?: 'card' | 'paypal',
    metadata?: SubscriptionMetadata,
    idempotencyKey?: string
): Promise<ApiResponse<{ clientSecret?: string; subscriptionId?: string }>> {
    await ensureFunnelAccessToken();

    const utms = getStoredUTMs();
    const body: any = {
        planId,
        ...(utms.utm_source && { utm_source: utms.utm_source }),
        ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
        ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
        ...(utms.utm_content && { utm_content: utms.utm_content }),
        ...(utms.utm_term && { utm_term: utms.utm_term }),
    };
    if (paymentMethodId) body.paymentMethodId = paymentMethodId;
    if (couponId) body.couponId = couponId;
    if (paymentType) body.paymentType = paymentType;
    body.metadata = buildMetadataWithUTMs(metadata);
    if (metadata?.ab_variant) body.ab_variant = metadata.ab_variant;

    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

    const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/subscription/4-week`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('4-week subscription error response:', errorData);
        throw new Error(
            getApiErrorMessage(errorData, `Failed to create 4-week subscription with status ${response.status}`)
        );
    }

    return response.json();
}

/**
 * Create a 12-week subscription that transitions to a follow-up plan.
 *
 * @param planId - The plan ID
 * @param paymentMethodId - Stripe payment method ID
 * @param transitionPlanId - The plan ID to transition to after the 12-week period
 * @param couponId - Optional coupon/discount ID
 * @returns ApiResponse with subscription data and optional clientSecret for 3DS
 */
export async function create12WeekSubscription(
    planId: string,
    paymentMethodId: string | undefined,
    transitionPlanId: string,
    couponId?: string,
    paymentType?: 'card' | 'paypal',
    metadata?: SubscriptionMetadata,
    idempotencyKey?: string
): Promise<ApiResponse<{ clientSecret?: string; subscriptionId?: string }>> {
    await ensureFunnelAccessToken();

    const utms = getStoredUTMs();
    const body: any = {
        planId,
        fourWeekPlanId: transitionPlanId,
        ...(utms.utm_source && { utm_source: utms.utm_source }),
        ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
        ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
        ...(utms.utm_content && { utm_content: utms.utm_content }),
        ...(utms.utm_term && { utm_term: utms.utm_term }),
    };
    if (paymentMethodId) body.paymentMethodId = paymentMethodId;
    if (couponId) body.couponId = couponId;
    if (paymentType) body.paymentType = paymentType;
    body.metadata = buildMetadataWithUTMs(metadata);
    if (metadata?.ab_variant) body.ab_variant = metadata.ab_variant;

    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;

    const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/subscription/12-week`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, `Failed to create 12-week subscription with status ${response.status}`));
    }

    return response.json();
}

/**
 * Create a special subscription (e.g. coaching plan) using an existing payment method on file.
 *
 * @param planId - The special plan ID
 * @returns ApiResponse with subscription data and optional clientSecret for 3DS
 */
export async function createSpecialSubscription(
    planId: string,
    idempotencyKey?: string
): Promise<ApiResponse<{ clientSecret?: string; subscriptionId?: string; appliedDiscount?: number; firstChargeAmount?: number }>> {
    const utms = getStoredUTMs();
    const headers: Record<string, string> = {};
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;
    const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/subscription/special`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            planId,
            ...(utms.utm_source && { utm_source: utms.utm_source }),
            ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
            ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
            ...(utms.utm_content && { utm_content: utms.utm_content }),
            ...(utms.utm_term && { utm_term: utms.utm_term }),
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errorData, `Failed to create special subscription with status ${response.status}`));
    }

    return response.json();
}

/**
 * Create a one-time payment for a feature unlock.
 *
 * @param planId - The feature unlock plan ID
 * @returns ApiResponse with payment data and optional clientSecret for 3DS
 */
export async function createFeatureUnlockPayment(
    planId: string,
    idempotencyKey?: string
): Promise<ApiResponse<{ clientSecret?: string; paymentIntentId?: string; status?: string }>> {
    const utms = getStoredUTMs();
    const headers = authHeaders();
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;
    const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/payment/feature-unlock`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            planId,
            ...(utms.utm_source && { utm_source: utms.utm_source }),
            ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
            ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
            ...(utms.utm_content && { utm_content: utms.utm_content }),
            ...(utms.utm_term && { utm_term: utms.utm_term }),
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create feature unlock payment with status ${response.status}`);
    }

    return response.json();
}

// ─── PayPal 2-step flow ─────────────────────────────────────────────────────

/**
 * DISABLED - PayPal unavailable on US-registered Stripe accounts.
 * PayPal via Stripe requires EEA/UK/Switzerland account registration.
 * Re-enable when using EU/UK Stripe account, or replace with
 * Braintree direct integration.
 * See: https://docs.stripe.com/payments/paypal
 */
/**
 * Create a SetupIntent for PayPal billing agreement authorization.
 *
 * @param planId - The subscription plan ID (required by backend to configure the SetupIntent)
 * @param planType - The plan type ('1_week_trial', '4_week', '12_week')
 * @param fourWeekPlanId - Optional transition plan ID for trial/12-week plans
 * @param metadata - Optional quiz metadata to attach
 * @returns ApiResponse with clientSecret, setupIntentId, and customerId
 */
export async function createPayPalSetupIntent(
    planId: string,
    planType: string,
    fourWeekPlanId?: string,
    metadata?: SubscriptionMetadata
): Promise<ApiResponse<{ clientSecret: string; setupIntentId: string; customerId: string }>> {
    const body: any = { planId, planType };
    if (fourWeekPlanId) body.fourWeekPlanId = fourWeekPlanId;
    if (metadata) body.metadata = metadata;

    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/paypal/setup-intent`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create PayPal setup intent with status ${response.status}`);
    }

    return response.json();
}

/**
 * DISABLED - PayPal unavailable on US-registered Stripe accounts.
 * PayPal via Stripe requires EEA/UK/Switzerland account registration.
 * Re-enable when using EU/UK Stripe account, or replace with
 * Braintree direct integration.
 * See: https://docs.stripe.com/payments/paypal
 */
/**
 * Create a subscription after PayPal authorization completes.
 *
 * @param planId - The subscription plan ID
 * @param paymentMethodId - The PayPal payment method ID from the confirmed SetupIntent
 * @param planType - The plan type ('1_week_trial', '4_week', '12_week')
 * @param fourWeekPlanId - Required for 1_week_trial and 12_week plans (transition plan)
 * @param couponId - Optional coupon/discount ID
 * @param metadata - Optional quiz metadata
 * @returns ApiResponse with subscriptionId, status, currentPeriodEnd
 */
export async function createPayPalSubscription(
    planId: string,
    paymentMethodId: string,
    planType: string,
    fourWeekPlanId?: string,
    couponId?: string,
    metadata?: SubscriptionMetadata
): Promise<ApiResponse<{ subscriptionId: string; status: string; currentPeriodEnd: string }>> {
    const body: any = { planId, paymentMethodId, planType };
    if (fourWeekPlanId) body.fourWeekPlanId = fourWeekPlanId;
    if (couponId) body.couponId = couponId;
    if (metadata) body.metadata = metadata;

    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/paypal/subscription`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create PayPal subscription with status ${response.status}`);
    }

    return response.json();
}

// ─── Gateway payment (2-step model) ─────────────────────────────────────────

/**
 * Create a gateway payment intent ($0.99 one-time charge to verify the card).
 *
 * @param planId - The gateway plan ID (fetched via getPlans('one_time', 'gateway'))
 * @returns ApiResponse with clientSecret and paymentIntentId
 */
export async function createGatewayPayment(
    planId: string
): Promise<ApiResponse<{ clientSecret: string; paymentIntentId: string }>> {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREATE_ONE_TIME_PAYMENT}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ planId }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create gateway payment with status ${response.status}`);
    }

    return response.json();
}

/**
 * Create the primary subscription after gateway payment succeeds.
 *
 * @param planId - The subscription plan ID
 * @param paymentMethodId - Optional Stripe payment method ID
 * @returns ApiResponse with subscriptionId and optional clientSecret for 3DS
 */
export async function createPrimarySubscription(
    planId: string,
    paymentMethodId?: string
): Promise<ApiResponse<{ subscriptionId?: string; clientSecret?: string }>> {
    const utms = getStoredUTMs();
    const body: Record<string, string> = {
        planId,
        ...(utms.utm_source && { utm_source: utms.utm_source }),
        ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
        ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
        ...(utms.utm_content && { utm_content: utms.utm_content }),
        ...(utms.utm_term && { utm_term: utms.utm_term }),
    };
    if (paymentMethodId) body.paymentMethodId = paymentMethodId;

    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/subscription/primary`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create primary subscription with status ${response.status}`);
    }

    return response.json();
}

/**
 * Get the current user's subscription/payment status.
 *
 * @returns ApiResponse with hasCompletedGatewayPayment, hasPrimarySubscription, etc.
 */
export async function getSubscriptionStatus(): Promise<ApiResponse<{
    hasCompletedGatewayPayment: boolean;
    hasPrimarySubscription: boolean;
    primarySubscription?: {
        _id?: string;
        stripeSubscriptionId?: string;
        status?: string;
    } | null;
}>> {
    await ensureFunnelAccessToken();
    const response = await fetchWithAuthRetry(`${API_CONFIG.BASE_URL}/stripe/subscription/status`, {
        method: 'GET',
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get subscription status with status ${response.status}`);
    }

    return response.json();
}

/**
 * Poll backend until gateway payment is confirmed by Stripe webhook.
 * Uses exponential-ish backoff (fixed delay between attempts).
 *
 * @param maxAttempts - Maximum number of poll attempts (default 15)
 * @param delayMs - Delay between attempts in ms (default 2000)
 * @returns true if confirmed within attempts, false if timed out
 */
export async function checkGatewayPaymentStatusWithInterval(
    maxAttempts = 15,
    delayMs = 2000
): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const res = await getSubscriptionStatus();
            if (res.success && res.data?.hasCompletedGatewayPayment) {
                return true;
            }
        } catch {
            // ignore individual poll failures
        }
        if (i < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
    }
    return false;
}

/**
 * Verify card has sufficient balance for the subscription amount.
 * Only used for manual card entry — digital wallets handle this themselves.
 *
 * @param amount - Amount in cents to verify
 * @param paymentMethodId - The Stripe payment method ID to verify
 * @returns ApiResponse with verified boolean and optional reason
 */
export async function verifyCardBalance(
    amount: number,
    paymentMethodId: string
): Promise<ApiResponse<{ verified: boolean; reason?: string }>> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/verify-balance`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amount, paymentMethodId }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to verify card balance with status ${response.status}`);
    }

    return response.json();
}

// ─── Payment methods ─────────────────────────────────────────────────────────

/**
 * Retrieve saved payment methods for the current user.
 *
 * @returns ApiResponse with array of payment method objects
 */
export async function getPaymentMethods(): Promise<ApiResponse<any[]>> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/payment-methods`, {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch payment methods with status ${response.status}`);
    }

    return response.json();
}

// ─── Subscription management ─────────────────────────────────────────────────

/**
 * Get all subscriptions for the current user.
 *
 * @returns ApiResponse with array of subscription objects
 */
export async function getUserSubscriptions(): Promise<ApiResponse<any[]>> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/subscriptions`, {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch subscriptions with status ${response.status}`);
    }

    return response.json();
}

/**
 * Cancel a subscription by its category.
 *
 * @param category - Subscription category ('primary' or 'special')
 * @param immediate - Whether to cancel immediately (true) or at period end (false)
 * @returns ApiResponse confirming cancellation
 */
export async function cancelSubscriptionByCategory(
    category: 'primary' | 'special',
    immediate: boolean
): Promise<ApiResponse> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/subscriptions/cancel`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ category, immediate }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to cancel subscription with status ${response.status}`);
    }

    return response.json();
}

/**
 * Reactivate a previously canceled subscription by its category.
 *
 * @param category - Subscription category ('primary' or 'special')
 * @returns ApiResponse with optional clientSecret if payment confirmation is needed
 */
export async function reactivateSubscriptionByCategory(
    category: 'primary' | 'special',
    couponId?: string
): Promise<ApiResponse<{ clientSecret?: string }>> {
    const body: { category: 'primary' | 'special'; couponId?: string } = { category };
    if (couponId) {
        body.couponId = couponId;
    }

    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/subscriptions/reactivate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.message || `Failed to reactivate subscription with status ${response.status}`);
        error.statusCode = response.status;
        throw error;
    }

    return response.json();
}

/**
 * Create a new subscription from a renewal (e.g. after a canceled/past_due subscription)
 * using a new payment method.
 *
 * @param planId - The plan ID to subscribe to
 * @param paymentMethodId - New Stripe payment method ID
 * @returns ApiResponse with optional clientSecret if 3DS is needed
 */
export async function createSubscriptionFromRenewal(
    planId: string,
    paymentMethodId: string
): Promise<ApiResponse<{ clientSecret?: string; subscriptionId?: string }>> {
    const utms = getStoredUTMs();
    const response = await fetch(`${API_CONFIG.BASE_URL}/stripe/subscriptions/renew`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
            planId,
            paymentMethodId,
            ...(utms.utm_source && { utm_source: utms.utm_source }),
            ...(utms.utm_medium && { utm_medium: utms.utm_medium }),
            ...(utms.utm_campaign && { utm_campaign: utms.utm_campaign }),
            ...(utms.utm_content && { utm_content: utms.utm_content }),
            ...(utms.utm_term && { utm_term: utms.utm_term }),
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.message || `Failed to create renewal subscription with status ${response.status}`);
        error.statusCode = response.status;
        error.code = errorData.code;
        throw error;
    }

    return response.json();
}
